import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, forkJoin, map, mergeMap, of } from 'rxjs';
import {
    CompanySegmentationManagementControllerService,
    CustomerSegmentationManagementControllerService
} from '@iota/inh-core-v1-management-client';
import {
    CustomerSegmentationActionTypes,
    LoadCustomerSegmentationOptionsSuccess,
    LoadCustomerSegmentationOptionsFailure,
    LoadCompanySegmentation,
    LoadCompanySegmentationSuccess,
    LoadCompanySegmentationFailure,
    CreateCompanySegmentation,
    CreateCompanySegmentationSuccess,
    CreateCompanySegmentationFailure,
    UpdateCompanySegmentation,
    UpdateCompanySegmentationSuccess,
    UpdateCompanySegmentationFailure
} from '../actions/customer-segmentation.actions';

@Injectable()
export class CustomerSegmentationEffects {
    constructor(
        private actions$: Actions,
        private store: Store,
        private companySegmentationService: CompanySegmentationManagementControllerService,
        private customerSegmentationService: CustomerSegmentationManagementControllerService
    ) {}

    loadCustomerSegmentationOptions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerSegmentationActionTypes.LoadCustomerSegmentationOptions),
            mergeMap(() =>
                forkJoin({
                    industries: this.customerSegmentationService.getAllCustomerSegmentationIndustries(),
                    tariffModels: this.customerSegmentationService.getAllCustomerSegmentationTariffModels(),
                    enterpriseSizes: this.customerSegmentationService.getAllCustomerSegmentationEnterpriseSizes(),
                    bgmMaturityLevels: this.customerSegmentationService.getAllCustomerSegmentationBgmMaturityLevels()
                }).pipe(
                    map(({ industries, tariffModels, enterpriseSizes, bgmMaturityLevels }) =>
                        LoadCustomerSegmentationOptionsSuccess({
                            industries,
                            tariffModels,
                            enterpriseSizes,
                            bgmMaturityLevels
                        })
                    ),
                    catchError((error) => of(LoadCustomerSegmentationOptionsFailure({ error })))
                )
            )
        )
    );

    loadCompanySegmentation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerSegmentationActionTypes.LoadCompanySegmentation),
            mergeMap((action: ReturnType<typeof LoadCompanySegmentation>) =>
                this.companySegmentationService.getCompanySegmentation(action.companyId).pipe(
                    map((segmentation) => LoadCompanySegmentationSuccess({ segmentation })),
                    catchError((error) => of(LoadCompanySegmentationFailure({ error })))
                )
            )
        )
    );

    createCompanySegmentation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerSegmentationActionTypes.CreateCompanySegmentation),
            mergeMap((action: ReturnType<typeof CreateCompanySegmentation>) =>
                this.companySegmentationService.createCompanySegmentation(action.companyId, action.request).pipe(
                    map((segmentation) => CreateCompanySegmentationSuccess({ segmentation })),
                    catchError((error) => of(CreateCompanySegmentationFailure({ error })))
                )
            )
        )
    );

    updateCompanySegmentation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomerSegmentationActionTypes.UpdateCompanySegmentation),
            mergeMap((action: ReturnType<typeof UpdateCompanySegmentation>) =>
                this.companySegmentationService.updateCompanySegmentation(action.companyId, action.request).pipe(
                    map((segmentation) => UpdateCompanySegmentationSuccess({ segmentation })),
                    catchError((error) => of(UpdateCompanySegmentationFailure({ error })))
                )
            )
        )
    );
}
